import { useCallback, useState } from 'react';
import { useTradeClient } from '../../../client/client-hook';
import useAuth from '../../../hooks/useAuth';
import { Log, MasterOrderInfo, SimulateTradeReply, SimulateTradeReq, SlaveOrder, UpdatedSlaveConfig } from '../../../proto/trade-copier_pb';

export type SimulateStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface SimulateInput {
  masterAccountId: string;
  slaveAccountId: string;
  slaveConfig: UpdatedSlaveConfig.AsObject;
  masterOrder: MasterOrderInfo.AsObject;
  slaveSymbol?: string;
}

interface SimulateState {
  status: SimulateStatus;
  data: SlaveOrder.AsObject | null;
  logs: Log.AsObject[];
  error: string | null;
}

const INITIAL_STATE: SimulateState = {
  status: 'idle',
  data: null,
  logs: [],
  error: null
};

export const useSimulateTrade = () => {
  const { user } = useAuth();
  const tradeClient = useTradeClient();
  const [state, setState] = useState<SimulateState>(INITIAL_STATE);

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  const simulate = useCallback(
    async (input: SimulateInput) => {
      if (!user?.tcId) {
        setState((s) => ({ ...s, status: 'error', error: 'User not authenticated' }));
        return;
      }

      setState((s) => ({ ...s, status: 'submitting', error: null }));

      try {
        const client = await tradeClient();
        const req: SimulateTradeReq.AsObject = {
          userId: user.tcId,
          masterAccount: input.masterAccountId,
          slaveAccount: input.slaveAccountId,
          slaveConfig: input.slaveConfig,
          masterOrder: input.masterOrder,
          slaveSymbol: input.slaveSymbol?.trim() ? { value: input.slaveSymbol.trim() } : undefined
        };

        const res = (await client.simulateTrade(req)) as SimulateTradeReply.AsObject;

        setState({
          status: 'success',
          data: res.orderInfo ?? null,
          logs: res.logList ?? [],
          error: null
        });
      } catch (err: any) {
        setState({
          status: 'error',
          data: null,
          logs: [],
          error: err?.message ?? 'Simulation failed'
        });
      }
    },
    [tradeClient, user?.tcId]
  );

  return {
    status: state.status,
    data: state.data,
    logs: state.logs,
    error: state.error,
    loading: state.status === 'submitting',
    simulate,
    reset
  };
};
